import { useEffect, useState } from "react";

// The organisation's look: logo, icon and the theme Fully Funded Shifts set in the head app (app name, colours,
// fonts; null for the built-in look). Fetched once from the public branding lookup (it is needed on the login and
// signing screens, before anyone is signed in) and shared by every <BrandLogo>.
const BASE = process.env.REACT_APP_BACKEND_URL || "";

let state = { loaded: false, logoUrl: null, iconUrl: null, theme: null };
const listeners = new Set();
let inflight = null;

// The built-in name, for an organisation Fully Funded Shifts hasn't set a theme for.
export const BUILT_IN_NAME = "Fully Funded Shifts";

// The browser tab: the organisation's name, its icon and its colour. index.html has the built-in ones. Never throws:
// a missing <head> element or an odd value just leaves the built-in one.
function applyToDocument(s) {
  try {
    if (s.theme?.app_name) document.title = s.theme.app_name;
    if (s.theme && s.iconUrl) {
      document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => {
        l.setAttribute("href", s.iconUrl);
        l.removeAttribute("type");
        l.removeAttribute("sizes");
      });
    }
    const colour = s.theme?.colours?.primary;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && /^#[0-9A-Fa-f]{6}$/.test(colour || "")) meta.setAttribute("content", colour);
  } catch { /* the built-in tab look stays */ }
}

const publish = (next) => {
  state = next;
  applyToDocument(state);
  listeners.forEach((fn) => fn(state));
};

export function refreshBranding() {
  if (!inflight) {
    inflight = fetch(`${BASE}/api/public/branding`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then((d) =>
        publish({
          loaded: true,
          logoUrl: d.logo_url ? `${BASE}${d.logo_url}` : null,
          iconUrl: d.icon_url ? `${BASE}${d.icon_url}` : null,
          theme: d.theme && typeof d.theme === "object" ? d.theme : null,
        }))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useBranding() {
  const [s, setS] = useState(state);
  useEffect(() => {
    listeners.add(setS);
    if (!state.loaded) refreshBranding();
    else setS(state);
    return () => listeners.delete(setS);
  }, []);
  return s;
}

// The organisation's name for screens ("Prydes Support"), or the built-in one. Empty until the lookup has answered,
// so the built-in name never flashes up on an organisation's own sign-in page.
export function useAppName() {
  const { loaded, theme } = useBranding();
  if (!loaded) return "";
  return theme?.app_name || BUILT_IN_NAME;
}

// Test hook: the state is module-level, so tests reset it between cases.
export function resetBrandingForTests() {
  state = { loaded: false, logoUrl: null, iconUrl: null, theme: null };
  inflight = null;
  listeners.clear();
}
